import React, {useEffect, useRef, useState} from 'react';
import {ControlPosition, MapControl, useMap, useMapsLibrary} from "@vis.gl/react-google-maps";
import Panel from "./components/panel.jsx"
import QuerySummary from "./components/query-summary.jsx";
import {useSpiderfier} from "../../../hooks/use-spiderfier.js";
import QueryBuilderModal from "./components/query-builder-modal.jsx";
import {
    getAnalysisItems,
    getFilterOptionValue,
    getFilterOptionValues,
    getMissingReasonKeys,
    getSelectedAnalysisKeys,
    itemUsesReasons,
} from "./components/analysis-items.js";
import {subDays, format, isAfter, parse} from "date-fns";
import ResultsDisplay from "./components/result-display.jsx";
import axios from "axios";
import projectUtils, {markRequiredFields} from "../../../utils/project-utils.js";
import {renderToStaticMarkup} from "react-dom/server";
import ItemsByPeriodComponent from "./components/items-by-period-component.jsx";
import ItemsByLayersComponent from "./components/items-by-layers-component.jsx";
import SubtitlesComponent from "./components/subtitlesComponent.jsx";
import {getAssetPath} from "../../../utils/get-asset-path.js";
import {createRoot} from "react-dom/client";
import Button from "../../../components/button/button.jsx"
import {toast} from "react-toastify";
import MarkerDetails from "./components/marker-details.jsx";
import {Modal} from "../../../components/modal/modal.jsx";

const getInitialFilters = () => ({
    dt_ini: format(subDays(new Date(), 30), "yyyy-MM-dd"),
    dt_end: format(new Date(), "yyyy-MM-dd"),
    georeferenced: true,
    without_coordinates: true,
    type_place: [],
    layers: [],
    points: [],
    items: {
        "sentry-occurrence": {reasons: [], cityFact: "", only_my_city: false},
        "dispatch-attendance": {reasons: []},
        "dispatch-bogcm": {reasons: []},
        "dispatch-dispatches": {reasons: []},
        "sos": {protection_type: [], status: ""},
        "encounter": {status: ""},
    },
});

let iwOpen;

const API_URL = import.meta.env.VITE_API_URL;

const AnalysisToolInit = ({isPrinting, hasDispatch, hasWaze, setIsLoading, boName, setFiltersToPrint, itemsByPeriod, itemsByLayers, subtitles, mapLayers, mapMarkers, markerTarget, setMarkerTarget}) => {
    const [target, setTarget] = useState([]);
    const [filters, setFilters] = useState(getInitialFilters);
    const [builderOpen, setBuilderOpen] = useState(false);
    const [hiddeResultDisplay, setHiddeResultDisplay] = useState(true);
    const [countTarget, setCountTarget] = useState({});
    const [applied, setApplied] = useState(false)
    const [modal, setModal] = useState({target: "", show: false});
    const [focusedTargetKey, setFocusedTargetKey] = useState("");
    const [invalidReasonKeys, setInvalidReasonKeys] = useState([]);
    const builderSnapshot = useRef(null);

    const map = useMap();
    const markerLib = useMapsLibrary("marker");
    const mapLib = useMapsLibrary("maps");
    const geometryLib = useMapsLibrary("geometry");
    const oms = useSpiderfier(map, mapMarkers);

    const closeModal = () => setModal({...modal, show: false});
    const modalTitle = {period: "Itens por período", layer: "Itens por setor", subtitles: "Legenda"}
    const modalBody = {period: itemsByPeriod.current, layer: itemsByLayers.current, subtitles: subtitles.current}

    const analysisItems = getAnalysisItems(hasDispatch, boName);
    const selectedTargetKeys = getSelectedAnalysisKeys(analysisItems, target);
    const selectedReasonTargetKeys = selectedTargetKeys.filter(itemUsesReasons);

    useEffect(() => {
        google.maps.event.addListenerOnce(map, 'tilesloaded', () => handleBuilderOpen(true));

        google.maps.event.addListener(map, 'click', () => {
            iwOpen?.close();
            iwOpen = undefined;
            oms.unspiderfy()

            setMarkerTarget(prev => {
                prev?.setAnimation(null);
                return undefined;
            });
        });
    }, []);

    const handleBuilderOpen = (open) => {
        if (open) {
            if (!builderOpen) builderSnapshot.current = {filters, target, focusedTargetKey, invalidReasonKeys};
            setBuilderOpen(true);
            return;
        }

        const snapshot = builderSnapshot.current;
        if (snapshot) {
            setFilters(snapshot.filters);
            setTarget(snapshot.target);
            setFocusedTargetKey(snapshot.focusedTargetKey);
            setInvalidReasonKeys(snapshot.invalidReasonKeys);
        }

        setBuilderOpen(false);
    };

    const resetFilters = () => {
        setFilters(getInitialFilters());
        setTarget([]);
        setFocusedTargetKey("");
        setInvalidReasonKeys([]);
    };

    const clearData = () => {
        [...mapLayers].forEach(([, layer]) => layer.setMap(null));
        [...mapMarkers].forEach(([, marker]) => marker.setMap(null));

        oms.unspiderfy();

        iwOpen?.close();

        mapLayers.clear();
        mapMarkers.clear();

        itemsByPeriod.current = null;
        itemsByLayers.current = null;
        subtitles.current = null;

        setMarkerTarget(prev => {
            prev?.setAnimation(null);
            return undefined;
        });

        setHiddeResultDisplay(true);
        setApplied(false)
    }

    const getSelectedReasons = () => {
        const map = {};
        selectedReasonTargetKeys.forEach(key => (filters.items[key]?.reasons ?? []).forEach(reason => {
            map[getFilterOptionValue(reason)] = reason;
        }));
        return Object.values(map);
    };

    const clearInvalidReason = (key) => {
        setInvalidReasonKeys(previous => previous.filter(invalidKey => invalidKey !== key));
    };

    const setMissingReasonState = (keys) => {
        setInvalidReasonKeys(keys);
        if (keys.length) setFocusedTargetKey(keys[0]);
    };

    const apply = () => {
        const startVal = filters.dt_ini;
        const endVal = filters.dt_end;
        const missingReasonKeys = getMissingReasonKeys(selectedReasonTargetKeys, filters.items);

        setMissingReasonState(missingReasonKeys);

        if (!startVal) {
            projectUtils.swalErrorMessage('', 'O campo "Data/hora inicial" é obrigatório.', 'Ok');
            return false;
        }

        if (!endVal) {
            projectUtils.swalErrorMessage('', 'O campo "Data/hora final" é obrigatório.', 'Ok');
            return false;
        }


        if (isAfter(startVal, endVal)) {
            projectUtils.swalErrorMessage('', 'A data inicial não pode ser maior que a data atual', 'Ok');
            return false;
        }

        if (!selectedTargetKeys.length) {
            projectUtils.swalErrorMessage("", "Selecione pelo menos um item de análise.", "Ok");
            return false;
        }

        if (missingReasonKeys.length) {
            projectUtils.swalErrorMessage("", "Selecione ao menos uma natureza nos itens selecionados.", "Ok", false, "Não", () => {
                markRequiredFields("Naturezas");
            });
            return false;
        }

        setInvalidReasonKeys([]);

        const reasonsByTarget = {};
        selectedReasonTargetKeys.forEach(key => {
            reasonsByTarget[key] = filters.items[key]?.reasons ?? [];
        });

        clearData();
        setIsLoading(true);

        const formData = new FormData();

        formData.append('initialDate', filters.dt_ini);
        formData.append('finalDate', filters.dt_end);
        formData.append('target', JSON.stringify(selectedTargetKeys));
        formData.append('layers', JSON.stringify(getFilterOptionValues(filters.layers)));
        formData.append('points', JSON.stringify(getFilterOptionValues(filters.points)));
        formData.append('typePlaces', JSON.stringify(getFilterOptionValues(filters.type_place)));
        formData.append('georeferenced', filters.georeferenced);
        formData.append('withoutCoordinates', filters.without_coordinates);
        formData.append('reasons', selectedReasonTargetKeys.length ? JSON.stringify(getSelectedReasons()) : JSON.stringify(null));

        Object.keys(reasonsByTarget).map(key => {
            formData.append(key + '-reasons', JSON.stringify(reasonsByTarget[key]));
        })

        if (selectedTargetKeys.includes("sentry-occurrence")) {
            const occurrence = filters.items["sentry-occurrence"];
            formData.append('cityFact', getFilterOptionValue(occurrence.cityFact));
            formData.append('onlyMyCity', occurrence.only_my_city);
        }

        if (selectedTargetKeys.includes("sos")) {
            const sos = filters.items["sos"];
            formData.append('protection_type', JSON.stringify(getFilterOptionValues(sos.protection_type)));
            formData.append('status', getFilterOptionValue(sos.status));
        }

        if (selectedTargetKeys.includes("encounter")) {
            const encounter = filters.items["encounter"];
            formData.append('encounter_status', getFilterOptionValue(encounter.status));
        }

        axios.post(`${API_URL}/analysisTool/apply`, formData)
            .then(({data: {data}}) => {
                const {areas, markers} = data;

                const bounds = new google.maps.LatLngBounds();
                setApplied(true)

                addAreaLayer(areas, bounds);
                addMarkers(markers, bounds);

                if (!mapMarkers.size) {
                    countTargetMarkers();
                    return;
                }

                map.fitBounds(bounds);
                if (map.getZoom() > 18) map.setZoom(18);

                setHiddeResultDisplay(false);

                countTargetMarkers();

                itemsByPeriod.current = renderToStaticMarkup(<ItemsByPeriodComponent markers={mapMarkers} boName={boName} />);
                itemsByLayers.current = renderToStaticMarkup(<ItemsByLayersComponent markers={mapMarkers} layers={mapLayers} boName={boName} geometryLib={geometryLib}/>);
                subtitles.current = renderToStaticMarkup(<SubtitlesComponent markers={mapMarkers} boName={boName} />);

                setFiltersToPrint(prepareFilters());
            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível carregar os dados.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false));
    }

    const prepareFilters = () => {
        const dateIni = parse(filters.dt_ini, 'yyyy-MM-dd', new Date());
        const dateEnd = parse(filters.dt_end, 'yyyy-MM-dd', new Date());

        const arrayFilters = [
            {text: "Data inicial", value: format(dateIni, 'dd/MM/yyyy')},
            {text: "Data final", value: format(dateEnd, 'dd/MM/yyyy')},
            {text: "Item com coordenadas", value: filters.georeferenced ? "Sim" : "Não"},
            {text: "Item sem coordenadas", value: filters.without_coordinates ? "Sim" : "Não"},
            {text: "Naturezas", value: getAmount(getSelectedReasons())},
            {text: "Tipos de local", value: getAmount(filters.type_place)},
            {text: "Setores", value: getAmount(filters.layers)},
            {text: "Pontos", value: getAmount(filters.points)},
        ];

        if (selectedTargetKeys.includes("sentry-occurrence")) {
            const occurrence = filters.items["sentry-occurrence"];

            arrayFilters.push(
                {text: "Município do fato", value: occurrence.cityFact?.value ?? ""},
                {text: "Apenas cadastradas pela minha corporação", value: occurrence.only_my_city ? "Sim" : ""},
            );
        }

        if (selectedTargetKeys.includes("sos")) {
            const sos = filters.items["sos"];

            arrayFilters.push(
                {text: "Programas de proteção", value: getAmount(sos.protection_type)},
                {text: "SOS Status", value: sos.status?.label ?? ""},
            );
        }

        if (selectedTargetKeys.includes("encounter")) {
            const encounter = filters.items["encounter"];
            arrayFilters.push({text: "Abordagem Status", value: encounter.status?.label ?? ""});
        }

        return arrayFilters.filter(item => item.value).map((item, i) =>
            <React.Fragment key={i}><strong>{item.text}:</strong> {item.value}  </React.Fragment>
        );
    }

    const getAmount = (button_) => {
        let amount = 0;
        let items = '';
        if (button_) {
            button_.forEach(function (button) {
                const text = button.label;
                if (amount < 1) items += (items === '' ? text : ', ' + text);

                amount = amount + 1;
            });
        }

        if ((amount - 1) >= 1) {
            items += ' mais ' + (amount - 1) + ' selecionada' + ((amount - 1) > 1 ? 's' : '');
        }

        return items;
    }

    const addAreaLayer = (areas, bounds) => {
        areas.forEach((area, i) => {
            const coordinates_ = JSON.parse(area.coordinates);

            let c = 1;
            const coordinates = [];
            let lat = undefined;
            coordinates_.map(function (lng) {
                if (c & 1) {
                    lat = lng;
                } else {
                    coordinates.push({lat, lng});
                    bounds.extend(new google.maps.LatLng(lat, lng));
                }

                c++;
            });

            const areaLayer = new google.maps.Polygon({
                paths: coordinates,
                strokeColor: area.color,
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: area.color,
                fillOpacity: 0.35,
                area: area,
                coordinates: coordinates,
            });

            areaLayer.setMap(map);
            mapLayers.set(i, areaLayer);
        });
    }

    const addMarkers = (markers, bounds) => {
        markers.forEach((marker, i) => {
            const location = new google.maps.LatLng(marker.lat, marker.lng);

            let icon = marker.icon;

            if (!icon.includes("keeper")) {
                if (marker.latLngNull) {
                    icon = getAssetPath("/img/markers/marker_interrogation_24x24.svg");
                } else {
                    icon = getAssetPath(icon)
                }
            }

            if (icon === null) {
                icon = getAssetPath("/img/markers/default.svg");
            }

            if (marker.description.icon_attachid) {
                icon = {
                    url: marker.icon,
                    scaledSize: new window.google.maps.Size(25, 25),
                };
            }

            const _marker = new markerLib.Marker({
                id: marker.id,
                position: location,
                title: marker.title,
                icon,
                description: marker.description,
                type: marker.type,
                map
            });

            _marker.addListener('click', () => {
                oms.spiderfy(_marker.id, () => {
                    setMarkerTarget(prev => {
                        prev?.setAnimation(null);
                        return prev === _marker ? undefined : _marker
                    });

                    _marker.setAnimation(google.maps.Animation.BOUNCE);
                });
            });

            if (marker.type === "sentry-occurrence") {
                const controlUI = document.createElement('div');
                const div = createRoot(controlUI);
                div.render(
                    <div className="p-2">
                        <Button target={"primary-outline"} onClick={() => changeLocation(marker.id)}>
                            Alterar localização
                        </Button>
                    </div>
                )

                _marker.addListener("rightclick", () => {
                    if (iwOpen) iwOpen.close();

                    iwOpen = new google.maps.InfoWindow({content: controlUI, headerDisabled: true});
                    iwOpen.open(map, _marker);

                    google.maps.event.addListener(iwOpen, 'close', () => iwOpen = undefined);
                });
            }

            oms.addMarker(_marker);
            bounds.extend(location);
            mapMarkers.set(marker.id, _marker);
        });
    }

    const changeLocation = (id) => {
        const marker = mapMarkers.get(id);
        marker.setDraggable(true);
        const originalPosition = marker.getPosition();

        if (iwOpen) iwOpen.close();

        const controlUI = document.createElement('div');
        const div = createRoot(controlUI);
        div.render(
            <div className="p-2">
                <Button target={"primary-outline"} onClick={() => saveLocation(id, originalPosition)} actionFeedbackType='save'>

                    Salvar localização
                </Button>
            </div>
        )

        iwOpen = new google.maps.InfoWindow({content: controlUI, headerDisabled: true});

        iwOpen.open(map, marker);
        google.maps.event.addListenerOnce(iwOpen, 'close', () => {
            iwOpen = undefined;
            marker.setPosition(mapMarkers.get(id).draggable ? originalPosition : mapMarkers.get(id).getPosition());
            marker.setDraggable(false);
        });
    }

    const saveLocation = (id, originalPosition) => {
        const marker = mapMarkers.get(id);
        const position = marker.getPosition();

        projectUtils.swalMessage("warning", "", "Deseja alterar a localização?", "Confirmar",
            true, "Cancelar",
            ({isConfirmed}) => {
                marker.setDraggable(false);
                iwOpen.close();

                if (!isConfirmed) {
                    marker.setPosition(originalPosition);
                    return
                }

                const formData = new FormData();
                formData.append('id', marker.id);
                formData.append('lat', position.lat());
                formData.append('lng', position.lng());

                axios.post(`${API_URL}/analysisTool/dragendMarker`, formData)
                    .then(() => {
                        toast.success("Salvo com sucesso")
                    })
                    .catch(error => {
                        console.error(error);
                        marker.setPosition(originalPosition);
                        let message = 'Não foi possível alterar a posição da ocorrência.';
                        projectUtils.swalErrorMessage('', message, 'Ok');
                    });
            })

    }

    const countTargetMarkers = () => {
        const targets = ["sentry-occurrence", "dispatch-attendance", "dispatch-bogcm", "dispatch-dispatches", "sos", "encounter"];

        let count = targets.reduce((acc, type) => {
            acc[type] = 0;
            return acc;
        }, {});

        [...mapMarkers].forEach(([, marker]) => {
            if (count.hasOwnProperty(marker.type)) {
                if (typeof count[marker.type] === 'number') {
                    count[marker.type] += 1;
                    if (count[marker.type] > 99) count[marker.type] = '+99';
                }
            }
        });

        setCountTarget(count);
    }

    const selectedItems = analysisItems.filter(item => selectedTargetKeys.includes(item.key));

    const periodLabel = (filters.dt_ini && filters.dt_end)
        ? `${format(parse(filters.dt_ini, "yyyy-MM-dd", new Date()), "dd/MM/yyyy")} – ${format(parse(filters.dt_end, "yyyy-MM-dd", new Date()), "dd/MM/yyyy")}`
        : "";

    const closeDetails = () => setMarkerTarget(prev => {
        prev?.setAnimation(null);
        return undefined;
    });

    const searchFromBuilder = () => {
        if (apply() !== false) setBuilderOpen(false);
    };

    return (
        <>
            {
                !isPrinting &&
                <>
                    <MapControl position={ControlPosition.LEFT_TOP}>
                        <Panel>
                            <QuerySummary
                                selectedItems={selectedItems}
                                periodLabel={periodLabel}
                                applied={applied}
                                countTarget={countTarget}
                                onEdit={() => handleBuilderOpen(true)}
                                onSearch={apply}
                                searchDisabled={!target.length}
                            />

                            <ResultsDisplay
                                markers={mapMarkers}
                                layers={mapLayers}
                                map={map}
                                hiddeResultDisplay={hiddeResultDisplay}
                                setModal={setModal}
                                markerTarget={markerTarget}
                                setMarkerTarget={setMarkerTarget}
                                hasWaze={hasWaze}
                                mapLib={mapLib}
                            />
                        </Panel>
                    </MapControl>

                    {
                        !hiddeResultDisplay && markerTarget &&
                        <MapControl position={ControlPosition.RIGHT_TOP}>
                            <div className="p-2">
                                <MarkerDetails marker={markerTarget} boName={boName} onClose={closeDetails}/>
                            </div>
                        </MapControl>
                    }
                </>
            }

            <QueryBuilderModal
                open={builderOpen}
                setOpen={handleBuilderOpen}
                filters={filters}
                setFilters={setFilters}
                target={target}
                setTarget={setTarget}
                focusKey={focusedTargetKey}
                setFocusKey={setFocusedTargetKey}
                resetFilters={resetFilters}
                apply={searchFromBuilder}
                hasDispatch={hasDispatch}
                boName={boName}                invalidReasonKeys={invalidReasonKeys}
                clearInvalidReason={clearInvalidReason}
            />

            <Modal open={modal.show} setOpen={closeModal} centered size="md" backdrop="static" title={modalTitle[modal.target]} keyboard={false} className={"w-5/12"}>
                <Modal.Body className={"pt-0!"} >
                    <div className={"max-h-[calc(100vh-220px)] overflow-y-auto"} dangerouslySetInnerHTML={{__html: modalBody[modal.target]}}/>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default AnalysisToolInit;